import ProtectedRouter from "@/context/auth/ProtectedRouter";
import { TenantProvider } from "@/context/TenantContext";
import { useTenant } from "@/context/useTenant";
import MainLayout from "@/layouts/MainLayout";
import StarterLayout from "@/layouts/StarterLayout";
import NotFoundPage from "@/pages/NotFoundPage";
import routes from "@/utils/routes";
// @ts-ignore - Property Portal routes (will be available when property portal is set up)
// import propertyPortalRoutes from "@/project/propertyPortal/utils/routes";
// // @ts-ignore - Property Portal App (will be available when property portal is set up)
// import PropertyPortalApp from "@/project/propertyPortal/app"; // 👈 Import your Property Portal App
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { ScrollToTopOnRouteChange } from "@/components/ScrollToTopOnRouteChange";
import { PWABadge } from "@/components/PWABadge";
import {
  KarunaAdminRoutes,
  KarunaEvaluatorRoutes,
  KarunaSchoolRoutes,
} from "@/project/karuna/App";
import { KarunaProviders } from "@/project/karuna/KarunaProviders";
import karunaRoutes from "@/project/karuna/utils/Routes";
import "@/project/karuna/index.css";
import "@/project/karuna/App.css";

function karunaSegmentPath(routePath: string): string {
  if (routePath === "*") {
    return "*";
  }
  return routePath.startsWith("/") ? routePath.slice(1) : routePath;
}

function App() {
  return (
    <TenantProvider>
      <AppWithRouter />
      <PWABadge />
    </TenantProvider>
  );
}

const AppWithRouter = () => {
  const { tenantId } = useTenant();

  if (!tenantId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold">Tenant ID Required</h1>
          <p className="text-gray-600">
            Set <code className="rounded bg-muted px-1">VITE_X_TENANTID</code>{" "}
            in your <code className="rounded bg-muted px-1">.env</code> file
            (static tenant for API requests).
          </p>
        </div>
      </div>
    );
  }

  const mainPublicRoutes =
    routes.find((m: any) => m.type === "public")?.routes ?? [];
  const mainAuthRoutes =
    routes.find((m: any) => m.type === "auth")?.routes ?? [];

  const publicRoutes = [...mainPublicRoutes];
  const authRoutes = [...mainAuthRoutes];

  const [karunaPublic, karunaAuth] = karunaRoutes;

  return (
    <BrowserRouter>
      <ScrollToTopOnRouteChange />
      <Routes>
        <Route element={<TopLevelComponent />}>
          {/* ================= PUBLIC ROUTES ================= */}
          {publicRoutes.map(({ path, element: Page }) => (
            <Route key={path} path={path} element={<StarterLayout />}>
              <Route index element={<Page />} />
            </Route>
          ))}

          {/* ================= KARUNA (no /tenantId/karuna prefix) ================= */}
          <Route element={<KarunaProviders />}>
            <Route path="home" element={<Navigate to="/" replace />} />
            <Route path="admin/*" element={<KarunaAdminRoutes />} />
            <Route path="school/*" element={<KarunaSchoolRoutes />} />
            <Route path="evaluator/*" element={<KarunaEvaluatorRoutes />} />
            {karunaPublic.routes.map(route =>
              route.path === "/" ? (
                <Route key={route.name} index element={<route.element />} />
              ) : (
                <Route
                  key={route.name}
                  path={karunaSegmentPath(route.path)}
                  element={<route.element />}
                />
              )
            )}
            {karunaAuth.routes.map(route => (
              <Route
                key={route.name}
                path={karunaSegmentPath(route.path)}
                element={<route.element />}
              />
            ))}
          </Route>

          {/* ================= PROTECTED ROUTES (includes root path) ================= */}
          <Route element={<ProtectedRouter />}>
            <Route element={<MainLayout />}>
              {authRoutes.map(({ path, element: Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}

              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const TopLevelComponent = () => {
  return <Outlet />;
};

export default App;
